import React, { useEffect, useState } from 'react';
import { ShieldAlert, RefreshCw, Trophy, UserX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MondrianButton } from './ui/MondrianButton';
import { MondrianPanel } from './ui/MondrianPanel';
type GameState = 'intro' | 'playing' | 'complete';
type CardType = 'pure' | 'heretic';
interface Card {
  id: number;
  type: CardType;
  isRevealed: boolean;
  isDestroyed: boolean;
  rotation: number; // For heretic elements
}
export function DiagonalPolice() {
  const [gameState, setGameState] = useState<GameState>('intro');
  const [cards, setCards] = useState<Card[]>([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [message, setMessage] = useState<string>('');
  const [wrongClicks, setWrongClicks] = useState(0);
  const [showDoesburg, setShowDoesburg] = useState(false);
  // Initialize Game
  const startGame = () => {
    const newCards: Card[] = Array.from({
      length: 12
    }, (_, i) => ({
      id: i,
      type: Math.random() > 0.4 ? 'heretic' : 'pure',
      isRevealed: false,
      isDestroyed: false,
      rotation: Math.random() * 360
    }));
    setCards(newCards);
    setScore(0);
    setTimeLeft(30);
    setWrongClicks(0);
    setShowDoesburg(false);
    setGameState('playing');
    setMessage('找出所有带斜线的画作！');
  };
  // Timer Logic
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0 && gameState === 'playing') {
      setGameState('complete');
    }
  }, [gameState, timeLeft]);
  // Card Click Handler
  const handleCardClick = (card: Card) => {
    if (card.isDestroyed || gameState !== 'playing') return;
    if (card.type === 'heretic') {
      // Correct click: Destroy heretic
      setCards(prev => prev.map(c => c.id === card.id ? {
        ...c,
        isDestroyed: true
      } : c));
      setScore(s => s + 1);
      const quotes = ['斜线是不被允许的存在！', '宇宙的宁静得以恢复。', '杜斯伯格的异端已被清除！', '只有垂直和水平才是永恒！'];
      setMessage(quotes[Math.floor(Math.random() * quotes.length)]);
    } else {
      // Wrong click: It's pure
      setWrongClicks(w => {
        const newCount = w + 1;
        if (newCount >= 3) {
          setShowDoesburg(true);
          setGameState('complete');
        }
        return newCount;
      });
      setMessage('这是纯粹的艺术！不要破坏它！');
    }
  };
  return <div className="space-y-8 relative" data-id="element-57">
      <div className="text-center space-y-2" data-id="element-58">
        <h2 className="text-2xl font-bold uppercase" data-id="element-59">工具五：斜线警探</h2>
        <p className="text-gray-600" data-id="element-60">找出并消灭所有带有斜线的异端画作！</p>
      </div>

      <div className="bg-red-50 border-l-4 border-mondrian-red p-4 text-sm text-gray-700 italic" data-id="element-61">
        "警告：发现异端！杜斯伯格试图用斜线破坏宇宙的平衡。作为蒙德里安的助手，请务必剔除所有斜线，只保留神圣的水平与垂直。"
      </div>

      <MondrianPanel className="min-h-[500px] relative overflow-hidden" borderWidth={8} data-id="element-62">
        {gameState === 'intro' && <div className="absolute inset-0 z-10 bg-white/90 flex flex-col items-center justify-center p-8 text-center space-y-6" data-id="element-63">
            <ShieldAlert className="w-20 h-20 text-mondrian-red" data-id="element-64" />
            <h3 className="text-3xl font-black uppercase" data-id="element-65">新造型主义保卫战</h3>
            <p className="max-w-md text-lg" data-id="element-66">
              在30秒内，点击所有带有
              <span className="text-mondrian-red font-bold" data-id="element-67">斜线</span>
              的画作将其消灭。
              <br data-id="element-68" />
              千万不要误伤纯粹的水平垂直画作！
            </p>
            <MondrianButton variant="red" onClick={startGame} className="text-xl px-8 py-4" data-id="element-69">
              开始巡逻
            </MondrianButton>
          </div>}

        {gameState === 'complete' && <div className="absolute inset-0 z-20 bg-white/95 flex flex-col items-center justify-center p-8 text-center space-y-6 animate-fade-in" data-id="element-70">
            {showDoesburg ? <>
                <div className="w-32 h-32 bg-mondrian-blue border-4 border-mondrian-black rotate-12 flex items-center justify-center mb-4" data-id="element-71">
                  <UserX className="w-16 h-16 text-white -rotate-12" data-id="element-72" />
                </div>
                <h3 className="text-3xl font-black text-mondrian-blue" data-id="element-73">
                  元素主义者！
                </h3>
                <p className="text-xl font-bold" data-id="element-74">"斜线是生命的活力！"</p>
                <p className="text-gray-600" data-id="element-75">
                  你似乎对斜线情有独钟...杜斯伯格欢迎你的加入。
                </p>
              </> : <>
                <Trophy className="w-20 h-20 text-mondrian-yellow" data-id="element-76" />
                <h3 className="text-3xl font-black" data-id="element-77">任务完成</h3>
                <p className="text-2xl font-bold" data-id="element-78">消灭异端: {score}</p>
                <p className="text-gray-600" data-id="element-79">蒙德里安对你的忠诚表示赞赏。</p>
              </>}
            <MondrianButton variant="black" onClick={startGame} className="mt-4" data-id="element-80">
              <RefreshCw className="w-4 h-4 mr-2" data-id="element-81" /> 再次挑战
            </MondrianButton>
          </div>}

        {/* Game Header */}
        <div className="flex justify-between items-center mb-6 border-b-4 border-mondrian-black pb-4" data-id="element-82">
          <div className="font-bold text-xl" data-id="element-83">
            时间:{' '}
            <span className={`${timeLeft < 10 ? 'text-mondrian-red' : ''}`} data-id="element-84">
              {timeLeft}s
            </span>
          </div>
          <div className="font-bold text-mondrian-red animate-pulse" data-id="element-85">
            {message}
          </div>
          <div className="font-bold text-xl" data-id="element-86">得分: {score}</div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-3 md:grid-cols-4 gap-4" data-id="element-87">
          {cards.map(card => <motion.div key={card.id} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => handleCardClick(card)} className={`
                aspect-square border-4 border-mondrian-black cursor-pointer relative overflow-hidden bg-white
                ${card.isDestroyed ? 'opacity-0 pointer-events-none' : 'opacity-100'}
              `} animate={card.isDestroyed ? {
          scale: 0,
          rotate: 180
        } : {
          scale: 1,
          rotate: 0
        }} data-id="element-88">
              {/* Base Grid (Pure) */}
              <div className="absolute inset-0 grid grid-cols-2 grid-rows-2" data-id="element-89">
                <div className={`border-r-4 border-b-4 border-black ${Math.random() > 0.5 ? 'bg-mondrian-red' : 'bg-white'}`} data-id="element-90"></div>
                <div className={`border-b-4 border-black ${Math.random() > 0.5 ? 'bg-mondrian-yellow' : 'bg-white'}`} data-id="element-91"></div>
                <div className={`border-r-4 border-black ${Math.random() > 0.5 ? 'bg-mondrian-blue' : 'bg-white'}`} data-id="element-92"></div>
                <div className="bg-white" data-id="element-93"></div>
              </div>

              {/* Heretic Element (Diagonal) */}
              {card.type === 'heretic' && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{
            transform: `rotate(${45}deg)`
          }} data-id="element-94">
                  <div className="w-[150%] h-4 bg-black" data-id="element-95"></div>
                  <div className="absolute w-4 h-[150%] bg-black" data-id="element-96"></div>
                </div>}
            </motion.div>)}
        </div>
      </MondrianPanel>
    </div>;
}